import {
  ProvisionPlaceBodySchema,
  UpdatePlaceBodySchema,
  type GetPlaceResponse,
  type ProvisionPlaceBody,
  type UpdatePlaceBody,
} from '@zrsystem/contracts';
import { z } from 'zod';

import { DEFAULT_BRAZIL_GEO } from '@/features/platform/constants/default-brazil-geo.js';
import { isValidCnpjDigits, normalizeCnpjDigits } from '@/features/platform/form/parse-cnpj.js';
import { sanitizeCepDigits } from '@/shared/form/parse-cep-input.js';

export const PLACE_TAX_REGIMES = ['SIMPLES_NACIONAL', 'LUCRO_PRESUMIDO', 'LUCRO_REAL'] as const;

export type PlaceTaxRegimeDraft = (typeof PLACE_TAX_REGIMES)[number];

export const PLACE_TAX_PAYER_TYPES = [...PLACE_TAX_REGIMES, 'MEI', 'OTHER'] as const;

export type PlaceTaxPayerTypeDraft = (typeof PLACE_TAX_PAYER_TYPES)[number];

export interface PlaceAnatomyDraft {
  name: string;
  slug: string;
  documentNumber: string;
  street: string;
  number: string;
  zipCode: string;
  isCentralDistribution: boolean;
  legalName: string;
  tradeName: string;
  stateRegistration: string;
  municipalRegistration: string;
  taxPayerType: PlaceTaxPayerTypeDraft | '';
  businessOpenedAt: string;
  complement: string;
  neighborhood: string;
  countryId: string;
  stateId: string;
  cityId: string;
  stateText: string;
  cityText: string;
  /** Optional source place for config snapshot (BR-PLT-11). Empty = none. */
  sourcePlaceId: string;
}

export function createEmptyPlaceAnatomyDraft(
  overrides: Partial<PlaceAnatomyDraft> = {},
): PlaceAnatomyDraft {
  return {
    name: '',
    slug: '',
    documentNumber: '',
    street: '',
    number: '',
    zipCode: '',
    isCentralDistribution: false,
    legalName: '',
    tradeName: '',
    stateRegistration: '',
    municipalRegistration: '',
    taxPayerType: '',
    businessOpenedAt: '',
    complement: '',
    neighborhood: '',
    countryId: DEFAULT_BRAZIL_GEO.countryId,
    stateId: '',
    cityId: '',
    stateText: '',
    cityText: '',
    sourcePlaceId: '',
    ...overrides,
  };
}

const slugSchema = z
  .string()
  .min(2, 'Informe um slug válido.')
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Use apenas letras minúsculas, números e hífens.');

const placeAnatomyDraftSchema = z.object({
  name: z.string().trim().min(1, 'Informe o nome da unidade.'),
  slug: slugSchema,
  documentNumber: z
    .string()
    .transform(normalizeCnpjDigits)
    .refine((digits) => digits.length === 14, 'Informe um CNPJ com 14 dígitos.')
    .refine(isValidCnpjDigits, 'CNPJ inválido. Verifique os dígitos.'),
  street: z.string().trim().min(1, 'Informe o logradouro.'),
  taxPayerType: z.enum(PLACE_TAX_REGIMES, {
    message: 'Selecione o regime tributário.',
  }),
});

const placeEditAnatomyDraftSchema = z.object({
  tradeName: z.string().trim().min(1, 'Informe o nome fantasia.'),
  legalName: z.string().trim().min(1, 'Informe a razão social.'),
  slug: slugSchema,
  documentNumber: z
    .string()
    .transform(normalizeCnpjDigits)
    .refine((digits) => digits.length === 14, 'Informe um CNPJ com 14 dígitos.')
    .refine(isValidCnpjDigits, 'CNPJ inválido. Verifique os dígitos.'),
  street: z.string().trim().min(1, 'Informe o logradouro.'),
  stateId: z.uuid('Informe UF via CEP (catálogo geo).'),
  cityId: z.uuid('Informe cidade via CEP (catálogo geo).'),
});

function fieldErrorsFromZod(error: z.ZodError): Record<string, string> {
  const fieldErrors: Record<string, string> = {};
  for (const issue of error.issues) {
    const path = issue.path.join('.') || '_form';
    fieldErrors[path] ??= issue.message;
  }
  return fieldErrors;
}

export function validatePlaceAnatomyDraft(draft: PlaceAnatomyDraft): Record<string, string> {
  const result = placeAnatomyDraftSchema.safeParse(draft);
  if (result.success) {
    return {};
  }
  return fieldErrorsFromZod(result.error);
}

export function validatePlaceEditAnatomyDraft(draft: PlaceAnatomyDraft): Record<string, string> {
  const result = placeEditAnatomyDraftSchema.safeParse(draft);
  if (result.success) {
    return {};
  }
  return fieldErrorsFromZod(result.error);
}

function emptyToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function resolveGeoId(draftId: string, fallback: string): string {
  return draftId.length > 0 ? draftId : fallback;
}

function buildAnatomyFromDraft(draft: PlaceAnatomyDraft) {
  const trimmedName = draft.name.trim();
  const zipDigits = sanitizeCepDigits(draft.zipCode);

  return {
    legalName: trimmedName,
    tradeName: trimmedName,
    documentNumber: normalizeCnpjDigits(draft.documentNumber),
    isCentralDistribution: draft.isCentralDistribution,
    address: {
      countryId: DEFAULT_BRAZIL_GEO.countryId,
      stateId: resolveGeoId(draft.stateId, DEFAULT_BRAZIL_GEO.stateId),
      cityId: resolveGeoId(draft.cityId, DEFAULT_BRAZIL_GEO.cityId),
      stateText: emptyToNull(draft.stateText),
      cityText: emptyToNull(draft.cityText),
      street: draft.street.trim(),
      number: emptyToNull(draft.number),
      complement: emptyToNull(draft.complement),
      neighborhood: emptyToNull(draft.neighborhood),
      zipCode: zipDigits.length > 0 ? zipDigits : null,
    },
  };
}

function buildEditAnatomyFromDraft(draft: PlaceAnatomyDraft) {
  const tradeName = draft.tradeName.trim();
  const taxPayerType = draft.taxPayerType === '' ? null : draft.taxPayerType;
  const zipDigits = sanitizeCepDigits(draft.zipCode);

  return {
    legalName: draft.legalName.trim(),
    tradeName,
    documentNumber: normalizeCnpjDigits(draft.documentNumber),
    stateRegistration: emptyToNull(draft.stateRegistration),
    municipalRegistration: emptyToNull(draft.municipalRegistration),
    businessOpenedAt: emptyToNull(draft.businessOpenedAt),
    taxPayerType,
    isCentralDistribution: draft.isCentralDistribution,
    address: {
      countryId: draft.countryId || DEFAULT_BRAZIL_GEO.countryId,
      stateId: draft.stateId,
      cityId: draft.cityId,
      stateText: emptyToNull(draft.stateText),
      cityText: emptyToNull(draft.cityText),
      street: draft.street.trim(),
      number: emptyToNull(draft.number),
      complement: emptyToNull(draft.complement),
      neighborhood: emptyToNull(draft.neighborhood),
      zipCode: zipDigits.length > 0 ? zipDigits : null,
      reference: null,
    },
  };
}

export function buildProvisionPlaceBody(draft: PlaceAnatomyDraft): ProvisionPlaceBody {
  const sourcePlaceId = draft.sourcePlaceId.trim();
  if (
    draft.taxPayerType !== 'SIMPLES_NACIONAL' &&
    draft.taxPayerType !== 'LUCRO_PRESUMIDO' &&
    draft.taxPayerType !== 'LUCRO_REAL'
  ) {
    throw new Error('Selecione o regime tributário (Simples, Presumido ou Real).');
  }
  const body = {
    name: draft.name.trim(),
    slug: draft.slug.trim().toLowerCase(),
    anatomy: {
      ...buildAnatomyFromDraft(draft),
      taxPayerType: draft.taxPayerType,
    },
    taxRegime: draft.taxPayerType,
    ...(sourcePlaceId.length > 0 ? { sourcePlaceId } : {}),
  };

  return ProvisionPlaceBodySchema.parse(body);
}

export function buildUpdatePlaceBody(draft: PlaceAnatomyDraft): UpdatePlaceBody {
  const tradeName = draft.tradeName.trim();
  const body = {
    name: tradeName,
    slug: draft.slug.trim().toLowerCase(),
    anatomy: buildEditAnatomyFromDraft(draft),
  };

  return UpdatePlaceBodySchema.parse(body);
}

export function placeAnatomyDraftFromResponse(place: GetPlaceResponse): PlaceAnatomyDraft {
  const { anatomy } = place;
  const { address } = anatomy;

  return createEmptyPlaceAnatomyDraft({
    name: place.name,
    slug: place.slug,
    documentNumber: anatomy.documentNumber,
    street: address.street,
    number: address.number ?? '',
    zipCode: address.zipCode ?? '',
    isCentralDistribution: place.isCentralDistribution,
    legalName: anatomy.legalName,
    tradeName: anatomy.tradeName,
    stateRegistration: anatomy.stateRegistration ?? '',
    municipalRegistration: anatomy.municipalRegistration ?? '',
    taxPayerType: anatomy.taxPayerType ?? '',
    businessOpenedAt: anatomy.businessOpenedAt ?? '',
    complement: address.complement ?? '',
    neighborhood: address.neighborhood ?? '',
    countryId: address.countryId ?? DEFAULT_BRAZIL_GEO.countryId,
    stateId: address.stateId ?? '',
    cityId: address.cityId ?? '',
    stateText: address.stateText ?? '',
    cityText: address.cityText ?? '',
  });
}
